// FCM 토큰 관리 훅
import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { getFCMToken, onMessageListener } from '@/utils/firebase';
import { useAuthStore, hasValidSessionToken } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { apiHelpers } from '@/api/core';
import { toast } from 'sonner';
import { showPushNotificationToast } from '@/utils/monitoringGroupColors';
import { monitoringKeys } from '@/api/monitoring/useMonitoring';

// 시리얼 넘버별 토스트 ID 관리
const activeToasts = new Map<string, string | number>();

export const useFCM = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { addPushNotification, showError, showSuccess } = useNotificationStore();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const lastRegisteredTokenRef = useRef<string | null>(null);
  // 탭 활성화 시 재등록(updatedAt 갱신) 쓰로틀용 타임스탬프
  const lastForceRefreshAtRef = useRef<number>(0);

  // 브라우저 지원 여부 확인
  useEffect(() => {
    const checkSupport = () => {
      // 안전하게 Notification 확인
      const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
      const hasServiceWorker = 'serviceWorker' in navigator;
      const hasPushManager = typeof window !== 'undefined' && 'PushManager' in window;

      const supported = hasServiceWorker && hasPushManager && hasNotification;

      setIsSupported(supported);

      if (supported && hasNotification) {
        try {
          const currentPermission = window.Notification.permission;
          setPermission(currentPermission);
        } catch (error) {
          console.warn('⚠️ Notification.permission 접근 실패:', error);
          setPermission('default');
        }
      } else {
        console.warn('❌ 브라우저가 푸시 알림을 지원하지 않습니다.');
        setPermission('default');
      }
    };
    checkSupport();

    // 권한 상태 변경 감지
    const checkPermission = () => {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        try {
          const currentPermission = window.Notification.permission;
          setPermission(() => {
            return currentPermission;
          });
        } catch (error) {
          // Notification 접근 실패 시 무시
        }
      }
    };

    // 주기적으로 권한 상태 확인 (권한이 변경될 수 있음)
    const interval = setInterval(checkPermission, 1000);

    return () => clearInterval(interval);
  }, []);

  // 알림 권한 요청
  const requestPermission = useCallback(async (): Promise<boolean> => {
    // 지원 여부를 다시 확인 (안전하게)
    const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = typeof window !== 'undefined' && 'PushManager' in window;
    const supported = hasServiceWorker && hasPushManager && hasNotification;

    if (!supported) {
      console.error('Browser does not support push notifications');
      showError('common:push_notification_not_supported');
      setIsSupported(false);
      return false;
    }

    setIsSupported(true); // 지원 여부 업데이트

    if (isRequestingPermission) {
      return false;
    }

    if (!hasNotification) {
      console.error('Notification API not available');
      return false;
    }

    try {
      setIsRequestingPermission(true);
      const permission = await window.Notification.requestPermission();
      setPermission(permission);
      setIsRequestingPermission(false);

      if (permission === 'granted') {
        showSuccess('common:push_notification_permission_granted');
        return true;
      } else {
        showError('common:push_notification_permission_denied');
        return false;
      }
    } catch (error) {
      setIsRequestingPermission(false);
      console.error('알림 권한 요청 실패:', error);
      showError('common:push_notification_permission_request_failed');
      return false;
    }
  }, [isRequestingPermission]);

  // 로그아웃 시에도 FCM 토큰을 유지한다(제거하지 않음).
  // 알림은 백엔드에서 플랫폼(데스크탑/모바일)별 "가장 최근에 활성화된" 기기 1대로 발송된다.
  // 따라서 사용자가 지금 보고 있는 브라우저가 항상 최신이 되도록, 로그인·탭 활성화 시점에
  // updatedAt을 강제로 재등록(refresh)한다. 그래야 그 브라우저가 발송 대상으로 선택돼 토스트가 뜬다.
  // 같은 브라우저에 다른 계정이 로그인하면 saveWebToken이 이전 계정 토큰을 자동 교체하며,
  // 미접속 기기는 TTL(30일)로 자연 만료된다.

  // FCM 토큰 발급 및 서버에 저장. force=true면 동일 토큰이어도 재전송해 updatedAt을 갱신한다.
  const registerToken = useCallback(
    async (force = false): Promise<string | null> => {
    // 지원 여부를 다시 확인 (안전하게)
    const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = typeof window !== 'undefined' && 'PushManager' in window;
    const supported = hasServiceWorker && hasPushManager && hasNotification;

    // 안전하게 permission 확인
    let currentPermission: NotificationPermission = 'default';
    if (hasNotification) {
      try {
        currentPermission = window.Notification.permission;
      } catch (error) {
        console.warn('⚠️ Notification.permission 접근 실패:', error);
      }
    }

    if ((!isAuthenticated && !hasValidSessionToken()) || !user) {
      return null;
    }

    if (!supported) {
      console.warn('Browser does not support push notifications');
      setIsSupported(false);
      return null;
    }

    setIsSupported(true); // 지원 여부 업데이트

    // 권한 확인
    if (hasNotification && currentPermission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) {
        console.warn('denied permission');
        return null;
      }
    }

    try {
      const fcmToken = await getFCMToken();
      if (!fcmToken) {
        console.warn('FCM token not found');
        return null;
      }

      // force가 아니면 동일 토큰 재전송을 생략(불필요한 요청 방지).
      // force면 updatedAt 갱신을 위해 재전송한다(현재 보고 있는 브라우저를 "최신"으로 승격).
      if (!force && lastRegisteredTokenRef.current === fcmToken) {
        setToken(fcmToken);
        return fcmToken;
      }

      // 서버에 토큰 저장 (origin + language 포함)
      try {
        const origin = window.location.origin;
        await apiHelpers.post('fcm.registerWebToken', {
          fcm_token: fcmToken,
          origin: origin,
          language: i18n.language || 'ko',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        });
        lastRegisteredTokenRef.current = fcmToken;
        setToken(fcmToken);
        return fcmToken;
      } catch (error: unknown) {
        console.error('[FCM] web-token registration failed:', error);
        setToken(fcmToken);
        return fcmToken;
      }
    } catch (error) {
      console.error('[FCM] registerToken failed:', error);
      return null;
    }
    },
    [isAuthenticated, user, requestPermission, i18n.language]
  );

  // 포그라운드 메시지 수신 처리
  useEffect(() => {
    const supported =
      'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

    if ((!isAuthenticated && !hasValidSessionToken()) || !supported) {
      return;
    }

    const unsubscribe = onMessageListener(payload => {
      const data = payload.data || {};

      // i18n 키이면 현재 언어로 번역, 아니면 그대로 사용
      const resolveText = (raw: string | undefined, fallbackKey: string | undefined) => {
        const key = raw || fallbackKey;
        if (!key) return undefined;
        if (!key.includes(':')) return key;
        // interpolation 변수 (data에 camelCase로 저장)
        const vars: Record<string, string> = {
          serialNumber: data.serialNumber || data.serial_number || '',
          siteName: data.siteName || data.site_name || '',
          sensorName: data.sensorName || data.sensor_name || '',
          alarmName: data.alarmName || data.alarm_name || '',
          warningReason: data.warningReason || data.warning_reason || '',
          companyName: data.companyName || data.company_name || '',
        };
        const translated = i18n.t(key, vars);
        return translated !== key ? translated : key;
      };

      const title = resolveText(
        payload.notification?.title || payload.data?.title,
        payload.data?.i18n_title
      );
      const body = resolveText(
        payload.notification?.body || payload.data?.body,
        payload.data?.i18n_body
      );

      addPushNotification({
        title,
        body,
        data: payload.data,
        source: 'foreground',
      });

      // 전역 알람 테두리/경보음 루프가 즉시 동기화되도록 라이브 집계를 바로 갱신
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      setTimeout(() => {
        console.log('[FCM] push history invalidate triggered');
        queryClient.invalidateQueries({ queryKey: monitoringKeys.all });
      }, 3000);

      const rawSerial = payload.data?.serial_number ?? payload.data?.serialNumber;
      const serialKey =
        typeof rawSerial === 'string'
          ? rawSerial
          : typeof rawSerial === 'number'
            ? String(rawSerial)
            : null;

      if (serialKey && activeToasts.has(serialKey)) {
        return;
      }

      if (title) {
        const toastId = showPushNotificationToast(
          { error: toast.error, warning: toast.warning, info: toast.info },
          title,
          body,
          data as Record<string, unknown> | undefined,
          { duration: 8000 }
        );

        const toastDedupeId =
          typeof toastId === 'string' || typeof toastId === 'number' ? toastId : Date.now();

        if (serialKey) {
          activeToasts.set(serialKey, toastDedupeId);
          setTimeout(() => {
            activeToasts.delete(serialKey);
          }, 8000);
        }
      } else {
        console.warn('[FCM] title이 없어 toast 미표시. payload:', payload);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isAuthenticated, addPushNotification, i18n, queryClient]);

  // 로그인·세션 유효 시 FCM 토큰 등록
  useEffect(() => {
    const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = typeof window !== 'undefined' && 'PushManager' in window;
    const supported = hasServiceWorker && hasPushManager && hasNotification;

    let currentPermission: NotificationPermission = 'default';
    if (hasNotification) {
      try {
        currentPermission = window.Notification.permission;
      } catch {
        /* ignore */
      }
    }

    const mayRegister =
      user &&
      supported &&
      !isRequestingPermission &&
      (isAuthenticated || hasValidSessionToken());

    if (!mayRegister) {
      return;
    }

    if (currentPermission === 'granted') {
      registerToken().catch(error => {
        console.warn('[FCM] failed to register token', error);
      });
    } else if (currentPermission === 'default') {
      requestPermission()
        .then(granted => {
          if (granted) {
            registerToken().catch(error => {
              console.warn('[FCM] failed to register token', error);
            });
          }
        })
        .catch(error => {
          console.warn('[FCM] failed to request permission', error);
        });
    }
  }, [isAuthenticated, user, permission, isRequestingPermission, registerToken, requestPermission]);

  useEffect(() => {
    const onSync = () => {
      // 로그인 직후엔 updatedAt을 강제 갱신해 이 브라우저를 발송 대상으로 승격
      lastForceRefreshAtRef.current = Date.now();
      registerToken(true).catch(error => {
        console.warn('[FCM] sync after login failed', error);
      });
    };
    window.addEventListener('bellcloud:fcm:sync', onSync);
    return () => window.removeEventListener('bellcloud:fcm:sync', onSync);
  }, [registerToken]);

  // 탭이 다시 활성화되면 이 브라우저의 토큰 updatedAt을 갱신한다.
  // 백엔드가 플랫폼별 "가장 최근 활성" 기기로만 발송하므로, 지금 보고 있는 브라우저가
  // 최신이 되어 푸시(=포그라운드 토스트)를 받도록 하기 위함. (과도한 요청 방지로 3분 쓰로틀)
  useEffect(() => {
    const REFRESH_THROTTLE_MS = 3 * 60 * 1000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (!user || (!isAuthenticated && !hasValidSessionToken())) {
        return;
      }
      // 권한이 이미 허용된 경우에만 갱신한다(탭 포커스 시 권한 팝업이 뜨는 것 방지).
      const granted =
        typeof window !== 'undefined' &&
        'Notification' in window &&
        window.Notification.permission === 'granted';
      if (!granted) {
        return;
      }
      const now = Date.now();
      if (now - lastForceRefreshAtRef.current < REFRESH_THROTTLE_MS) {
        return;
      }
      lastForceRefreshAtRef.current = now;
      registerToken(true).catch(error => {
        console.warn('[FCM] visibility refresh failed', error);
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [registerToken, user, isAuthenticated]);

  return {
    token,
    isSupported,
    permission,
    requestPermission,
    registerToken,
  };
};
